import { Fragment, FunctionComponent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";

import { Knob } from "primereact/knob";
import { ProgressSpinner } from "primereact/progressspinner";

import { faRotate, faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useFormatCurrencyHook } from "../../../../../utils/hooks/formatCurrencyHook";
import { translationKeys } from "../../../../models/app-translation-keys";
import { useCurrentUserService } from "../../../../services/ServiceHooks";
import { Device } from "../../../../stores";
import { useStore } from "../../../../stores/StoreContext";
import { TooltipWrapper } from "../../../Shared/Components/TooltipWrapper/TooltipWrapper";

import "./CurrentUserBudgetInfo.scss";

const budgetWarningColor = "#ff9800";
const budgetDangerColor = "#f44336";
const budgetRangeColor = "#d4dbe3";

function getWhitelabelBrandColor(): string {
  return getComputedStyle(document.documentElement).getPropertyValue("--customer-brand-color-background")?.trim();
}

const budgetDefaultColor = getWhitelabelBrandColor();

const CurrentUserBudgetInfoRaw: FunctionComponent = () => {
  const { t } = useTranslation();
  const { budgetStore, breakpointStore } = useStore();
  const currentUserService = useCurrentUserService();
  const [isBudgetInfoLoading, setIsBudgetInfoLoading] = useState(false);

  const formatCurrency = useFormatCurrencyHook();

  // Currently hardcoding currency to EUR until multi-currency support is implemented
  const currency = "EUR";

  const refreshBudgetInfo = async () => {
    setIsBudgetInfoLoading(true);
    await currentUserService.ensureHasBudget();
    setIsBudgetInfoLoading(false);
  };

  const usageColor = useCallback(() => {
    const usage = budgetStore.budgetInfoValue.currentUsage ?? 0;
    const limit = budgetStore.budgetInfoValue.budgetLimit ?? 1;
    const usagePercentage = (usage / limit) * 100;
    if (usagePercentage >= 90) {
      return budgetDangerColor;
    } else if (usagePercentage >= 75) {
      return budgetWarningColor;
    } else {
      return getWhitelabelBrandColor();
    }
  }, [budgetStore.budgetInfoValue]);

  const knobSize = useCallback(() => {
    return breakpointStore.isSmallerThan(Device.Desktop) ? 20 : 26;
  }, [breakpointStore.device]);

  const knobStrokeWidth = useCallback(() => {
    return breakpointStore.isSmallerThan(Device.Desktop) ? 18 : 20;
  }, [breakpointStore.device]);

  if (!budgetStore.budgetInfoValue.currentUsage && !budgetStore.budgetInfoValue.budgetLimit) {
    return (
      <div className="current-user-budget-info" id="currentUsageKnobError">
        <div className="error">
          <TooltipWrapper
            targetRef="#currentUsageKnobError"
            position="bottom"
            content={t(translationKeys.budget.failedToLoadBudgetInfo)}
          />
          {t(translationKeys.budget.failedToLoadBudgetInfo)}
        </div>
      </div>
    );
  }

  return (
    <div className="current-user-budget-info" id="currentUsageKnob">
      <TooltipWrapper
        targetRef="#currentUsageKnob"
        position="bottom"
        content={t(
          budgetStore.budgetDisabled
            ? translationKeys.budget.budgetDisabledInfoTooltip
            : translationKeys.budget.currentBudgetInfoTooltip,
          {
            currentUsage: formatCurrency(budgetStore.budgetInfoValue.currentUsage ?? 0, currency),
            budgetLimit: formatCurrency(budgetStore.budgetInfoValue.budgetLimit ?? 0, currency),
            remainingDays: budgetStore.remainingBudgetDays,
          },
        )}
      />

      {budgetStore.budgetDisabled && (
        <div className="exclamation-icon">
          <FontAwesomeIcon icon={faTriangleExclamation} color={budgetDangerColor} />
        </div>
      )}

      <div className="label" style={{ color: usageColor() }}>
        {formatCurrency(budgetStore.budgetInfoValue.currentUsage ?? 0, currency)}
      </div>

      <div className="budget-knob-container">
        {!isBudgetInfoLoading && (
          <Fragment>
            <Knob
              className="budget-knob"
              step={budgetStore.budgetInfoValue.currentUsage ?? 0}
              max={budgetStore.budgetInfoValue.budgetLimit ?? 1}
              size={knobSize()}
              value={budgetStore.budgetInfoValue.currentUsage ?? 0}
              valueTemplate=""
              strokeWidth={knobStrokeWidth()}
              rangeColor={budgetRangeColor}
              valueColor={usageColor()}
            />

            <FontAwesomeIcon
              icon={faRotate}
              className="budget-refresh-icon"
              onClick={refreshBudgetInfo}
              title={t(translationKeys.budget.refreshBudgetInfo)}
              color={budgetDefaultColor}
            />
          </Fragment>
        )}
        {isBudgetInfoLoading && <ProgressSpinner className="small-inline-spinner" color={budgetDefaultColor} />}
      </div>
    </div>
  );
};

export const CurrentUserBudgetInfo = observer(CurrentUserBudgetInfoRaw);
